/**
 * Dice display components for the replay narrative panel.
 *
 * BlockDie: renders a styled block-die face square (POW/push/skull/both-down).
 * DiceResult: renders a styled d6 result square (pass=green bg, fail=red bg).
 * parseDiceFromMechanical: extracts dice data from detailMechanical value strings.
 */

import { RulePopover } from "@/components/rule-popover"

// ─── Block Die ───────────────────────────────────────────────────────────────

export type BlockDieFace = "pow" | "push" | "skull" | "both-down"

const BLOCK_DIE_SYMBOLS: Record<BlockDieFace, string> = {
  pow: "!",
  push: "→",
  skull: "☠",
  "both-down": "⇅",
}

const BLOCK_DIE_COLORS: Record<BlockDieFace, string> = {
  pow: "oklch(0.60 0.22 28)", // red
  push: "oklch(0.60 0.12 85)", // gold
  skull: "oklch(0.50 0.01 230)", // muted
  "both-down": "oklch(0.55 0.15 310)", // purple
}

interface BlockDieProps {
  face: BlockDieFace
}

/**
 * Renders a single block die face as an 18×18px inline square.
 */
export function BlockDie({ face }: BlockDieProps) {
  return (
    <RulePopover category="block_die" ruleKey={face}>
      <span
        data-testid="block-die"
        data-face={face}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: "18px",
          height: "18px",
          fontSize: "10px",
          fontWeight: 900,
          background: "oklch(0.14 0.005 230)",
          border: `1px solid oklch(0.20 0.01 230)`,
          color: BLOCK_DIE_COLORS[face],
          flexShrink: 0,
          verticalAlign: "middle",
          cursor: "pointer",
        }}
        aria-label={`Block die: ${face}`}
      >
        {BLOCK_DIE_SYMBOLS[face]}
      </span>
    </RulePopover>
  )
}

// ─── D6 Result ───────────────────────────────────────────────────────────────

interface DiceResultProps {
  value: number | string
  passed: boolean
}

/**
 * Renders a single d6 result as an 18×18px inline square.
 * Pass: green background; Fail: red background.
 */
export function DiceResult({ value, passed }: DiceResultProps) {
  const style: React.CSSProperties = passed
    ? {
        background: "oklch(0.25 0.08 145 / 0.4)",
        color: "oklch(0.70 0.15 145)",
        border: "1px solid oklch(0.40 0.12 145)",
      }
    : {
        background: "oklch(0.25 0.08 28 / 0.4)",
        color: "oklch(0.65 0.18 28)",
        border: "1px solid oklch(0.40 0.14 28)",
      }

  return (
    <span
      data-testid="dice-result"
      data-passed={passed ? "true" : "false"}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: "18px",
        height: "18px",
        fontSize: "10px",
        fontWeight: 900,
        flexShrink: 0,
        verticalAlign: "middle",
        ...style,
      }}
      aria-label={`${passed ? "Pass" : "Fail"}: ${value}`}
    >
      {value}
    </span>
  )
}

// ─── Parsed Dice Data ─────────────────────────────────────────────────────────

export type ParsedDice =
  | { kind: "block"; faces: BlockDieFace[] }
  | { kind: "d6"; value: string; passed: boolean }
  | { kind: "none" }

/**
 * Attempts to extract dice result data from a mechanical value string.
 *
 * Patterns detected (case-insensitive):
 * - Block dice faces: "POW", "Push", "Skull", "Both Down"
 * - D6 results: single digit or "N+" pattern with pass/fail context
 *
 * Returns `{ kind: "none" }` when the string doesn't contain recognisable dice.
 */
export function parseDiceFromMechanical(value: string): ParsedDice {
  const lower = value.toLowerCase()

  // ─── Block die face detection ───
  // Detect one or more block die results in the string (POW, Push, Skull, Both Down)
  const blockFaces: BlockDieFace[] = []

  // These patterns may appear multiple times (e.g. "POW, Skull")
  const blockPatterns: [RegExp, BlockDieFace][] = [
    [/\bpow\b/g, "pow"],
    [/\bpush\b/g, "push"],
    [/\bskull\b/g, "skull"],
    [/\bboth.?down\b/g, "both-down"],
  ]

  for (const [pattern, face] of blockPatterns) {
    const matchCount = (lower.match(pattern) ?? []).length
    blockFaces.push(...Array.from({ length: matchCount }, () => face))
  }

  if (blockFaces.length > 0) {
    return { kind: "block", faces: blockFaces }
  }

  // ─── D6 result detection ───
  // Look for patterns like "Rolled 4 vs 3+ — success" or "4 (needed 3+)"
  const d6Match = /\brolled\s+(\d+)\b/i.exec(value)
  if (d6Match) {
    const rolled = d6Match[1]
    const passed =
      lower.includes("success") ||
      lower.includes("made") ||
      lower.includes("caught") ||
      lower.includes("pass")
    const failed =
      lower.includes("fail") ||
      lower.includes("miss") ||
      lower.includes("turnover")
    // Only emit a d6 dice result if we have a clear pass/fail signal
    if (passed || failed) {
      return { kind: "d6", value: rolled, passed }
    }
  }

  return { kind: "none" }
}
